import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as Plot from "@observablehq/plot";
import { api } from "../lib/api";
import { ymJp } from "../lib/format";
import {
  fiscalYearOf,
  fyMonthDates,
} from "../lib/fiscalYear";
import { useFilters } from "../store";
import { PlotFigure } from "./Plot";
import { InpatientFeePeriodControl } from "./InpatientFeePeriodControl";

const CATEGORIES = [
  "特定集中",
  "ハイケア",
  "回復期",
  "急性期一般",
  "特別入院基本料",
  "短期滞在3",
] as const;
const ROOM_CATEGORIES = [...CATEGORIES, "会計なし"] as const;
const SHORT_STAY3_START_MONTH = "202606";
const monthCode = (month: string) => month.slice(0, 7).replace("-", "");
const CATEGORY_COLORS: Record<string, string> = {
  特定集中: "#7c3aed",
  ハイケア: "#2563eb",
  回復期: "#0d9488",
  急性期一般: "#059669",
  特別入院基本料: "#e11d48",
  短期滞在3: "#d97706",
  会計なし: "#94a3b8",
};
const WARD_COLORS: Record<string, string> = {
  ICU: "#7c3aed",
  ハイケア: "#2563eb",
  "5D": "#0d9488",
  一般病床: "#d97706",
};
const HOSPITAL_WARD_GROUPS = [
  { unit: "ICU", sourceUnits: ["ICU"] },
  { unit: "ハイケア", sourceUnits: ["CCU", "HCU"] },
  { unit: "5D", sourceUnits: ["5D"] },
  { unit: "一般病床", sourceUnits: ["一般病床"] },
] as const;
export const INPATIENT_FEE_UNITS = [
  "ICU",
  "CCU",
  "HCU",
  "5D",
  "一般病床",
] as const;
const UNITS = INPATIENT_FEE_UNITS;
type Unit = (typeof UNITS)[number];
export type InpatientFeeView = "hospital" | Unit;

const yen = (v: number) => `${Math.round(v).toLocaleString()}円`;
const manYen = (v: number) =>
  `${(v / 10_000).toLocaleString(undefined, { maximumFractionDigits: 0 })}万`;
const manYen1 = (v: number) =>
  `${(v / 10_000).toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}万`;
const feeAmount = (v: number) =>
  // [2026-08-21 ユーザ指示] 億は小数2位まで
  `${(v / 100_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}億`;
const pct = (v: number | null) =>
  v == null || !Number.isFinite(v) ? "—" : `${(v * 100).toFixed(1)}%`;
const int = (v: number) => Math.round(v).toLocaleString();
const signed = (v: number, suffix = "") =>
  `${v > 0 ? "+" : ""}${Math.round(v).toLocaleString()}${suffix}`;
const monthLabel = (v: string) => ymJp(v.slice(0, 10));
const wardLabel = (u: string) => (u === "一般病床" ? "急性期一般" : u);
const unitLabel = (u: Unit) => wardLabel(u);
const expectedCategory = (u: Unit) =>
  u === "ICU"
    ? "特定集中"
    : u === "5D"
      ? "回復期"
      : u === "一般病床"
        ? "急性期一般"
        : "ハイケア";
const expectedCategoryLabel = (u: Unit) =>
  u === "一般病床" ? "急性期一般・短期滞在3" : expectedCategory(u);

export function InpatientFeeSidebar({
  dateMin,
  dateMax,
  view,
  onViewChange,
}: {
  dateMin: string;
  dateMax: string;
  view: InpatientFeeView;
  onViewChange: (view: InpatientFeeView) => void;
}) {
  const nav: { key: InpatientFeeView; label: string; sub: string }[] = [
    { key: "hospital", label: "病院全体", sub: "病床・入院料" },
    ...UNITS.map((unit) => ({
      key: unit,
      label: unitLabel(unit),
      sub: unit === "5D" ? "回復期・特別" : expectedCategoryLabel(unit),
    })),
  ];
  return (
    <aside className="card h-fit p-3 space-y-4">
      <div className="text-sm font-semibold text-slate-800">入院料分析</div>
      <section className="border-t border-slate-200 pt-3">
        <InpatientFeePeriodControl dateMin={dateMin} dateMax={dateMax} />
      </section>
      <nav
        className="border-t border-slate-200 pt-2 space-y-1"
        aria-label="入院料分析ページ"
      >
        {nav.map((n) => (
          <button
            key={n.key}
            type="button"
            onClick={() => onViewChange(n.key)}
            className={`w-full text-left rounded px-2.5 py-2 transition ${view === n.key ? "bg-indigo-50 text-indigo-950 shadow-[inset_3px_0_0_#4f46e5]" : "hover:bg-slate-50 text-slate-600"}`}
          >
            <div className="text-sm font-semibold">{n.label}</div>
            <div className="text-[10px] mt-0.5 opacity-70">{n.sub}</div>
          </button>
        ))}
      </nav>
    </aside>
  );
}

export function InpatientFeeAnalysisPanel({
  view,
}: {
  view: InpatientFeeView;
}) {
  const f = useFilters();
  const q = useQuery({
    queryKey: ["inpatient_fee_analysis", f.periodFrom, f.periodTo],
    queryFn: () =>
      api.inpatientFeeAnalysis({ dateFrom: f.periodFrom, dateTo: f.periodTo }),
  });

  if (q.isLoading)
    return (
      <div className="card p-6 text-sm text-slate-500">
        入院料分析を読み込み中…
      </div>
    );
  if (q.error || !q.data)
    return (
      <div className="card p-6 text-sm text-red-700">
        入院料分析を取得できません: {(q.error as Error)?.message}
      </div>
    );

  return (
    <div className="space-y-3">
      {view === "hospital" ? (
        <HospitalPage
          data={q.data}
          dateFrom={f.periodFrom}
          dateTo={f.periodTo}
        />
      ) : (
        <UnitPage
          unit={view}
          data={q.data}
          dateFrom={f.periodFrom}
          dateTo={f.periodTo}
        />
      )}
    </div>
  );
}

type Data = NonNullable<
  ReturnType<typeof useQuery> extends never
    ? never
    : Awaited<ReturnType<typeof api.inpatientFeeAnalysis>>
>;
type CategoryMap = Map<
  string,
  Map<string, { revenue: number; patientDays: number; cases: number }>
>;

function HospitalPage({
  data,
  dateFrom,
  dateTo,
}: {
  data: Data;
  dateFrom: string;
  dateTo: string;
}) {
  const months = [...new Set(data.occupancy.map((r) => r.month))]
    .filter((month) => month >= dateFrom && month <= dateTo)
    .sort();
  const fy = fiscalYearOf(dateTo);
  const trendMonths = fyMonthDates(fy);
  const currentTrendMonths = trendMonths.filter(
    (month) => month <= dateTo && months.includes(month),
  );
  const latestMonth = months.at(-1) ?? "";
  const [selectedMonth, setSelectedMonth] = useState(latestMonth);
  const selected = months.includes(selectedMonth) ? selectedMonth : latestMonth;
  const byMonth = new Map<
    string,
    {
      bedDays: number;
      midnightPatientDays: number;
      denominatorPatientDays: number;
    }
  >();
  for (const r of data.occupancy) {
    const x = byMonth.get(r.month) ?? {
      bedDays: 0,
      midnightPatientDays: 0,
      denominatorPatientDays: 0,
    };
    x.bedDays += r.bedDays;
    x.midnightPatientDays += r.midnightPatientDays;
    x.denominatorPatientDays += r.denominatorPatientDays;
    byMonth.set(r.month, x);
  }
  const allOccupancySeries = months.map((month) => {
    const r = byMonth.get(month)!;
    const rate = r.bedDays ? r.midnightPatientDays / r.bedDays : null;
    return {
      month,
      ...r,
      rate: rate == null ? null : rate * 100,
      rateLabel: pct(rate),
    };
  });
  const occupancySeries = allOccupancySeries.filter((r) =>
    currentTrendMonths.includes(r.month),
  );
  const revenueRows = data.monthly.map((r) => ({
    month: r.month,
    category: r.category,
    revenue: r.revenue,
  }));
  const revenueTotals = currentTrendMonths.map((month) => ({
    month,
    revenue: revenueRows
      .filter((r) => r.month === month)
      .reduce((s, r) => s + r.revenue, 0),
  }));
  const priorMonth = (month: string) =>
    `${Number(month.slice(0, 4)) - 1}${month.slice(4)}`;
  const priorOccupancySeries = trendMonths
    .map((month) => {
      const previousMonth = priorMonth(month);
      const r = byMonth.get(previousMonth);
      const rate = r && r.bedDays ? r.midnightPatientDays / r.bedDays : null;
      return { month, previousMonth, rate: rate == null ? null : rate * 100 };
    })
    .filter((r) => r.rate != null);
  const priorRevenueTotals = trendMonths
    .map((month) => ({
      month,
      previousMonth: priorMonth(month),
      revenue: revenueRows
        .filter((r) => r.month === priorMonth(month))
        .reduce((s, r) => s + r.revenue, 0),
    }))
    .filter((r) => r.revenue > 0);
  const selectedOccupancy = occupancySeries.filter((r) => r.month === selected);
  const selectedRevenue = revenueTotals
    .filter((r) => r.month === selected)
    .map((r) => ({ ...r, label: feeAmount(r.revenue) }));
  const selectedWards = HOSPITAL_WARD_GROUPS.map((group) => {
    const patientDays = data.occupancy
      .filter(
        (r) =>
          r.month === selected &&
          (group.sourceUnits as readonly string[]).includes(r.unit),
      )
      .reduce((s, r) => s + r.denominatorPatientDays, 0);
    const feeRevenue = data.roomDetail
      .filter(
        (r) =>
          r.month === selected &&
          group.sourceUnits.some((u) => r.room === (u === "5D" ? "5D病棟" : u)),
      )
      .reduce((s, r) => s + r.revenue, 0);
    return { unit: group.unit, patientDays, feeRevenue };
  });
  const selectedWardsForStack = [...selectedWards].sort((a, b) => {
    const order = ["ICU", "ハイケア", "一般病床", "5D"];
    return order.indexOf(a.unit) - order.indexOf(b.unit);
  });
  const tableWardGroups = [...HOSPITAL_WARD_GROUPS].sort((a, b) => {
    const order = ["ICU", "ハイケア", "一般病床", "5D"];
    return order.indexOf(a.unit) - order.indexOf(b.unit);
  });
  const tableMonths = months.slice(-12);
  const tableRows = ["月平均", ...[...tableMonths].reverse()].map((month) => {
    const isAverage = month === "月平均";
    const avg = (values: number[]) =>
      values.length ? values.reduce((s, v) => s + v, 0) / values.length : 0;
    const groups = tableWardGroups.map((group) => {
      const patientDays = tableMonths.map((m) =>
        data.occupancy
          .filter(
            (r) =>
              r.month === m &&
              (group.sourceUnits as readonly string[]).includes(r.unit),
          )
          .reduce((s, r) => s + r.denominatorPatientDays, 0),
      );
      const fee = tableMonths.map((m) =>
        data.roomDetail
          .filter(
            (r) =>
              r.month === m &&
              group.sourceUnits.some(
                (u) => r.room === (u === "5D" ? "5D病棟" : u),
              ),
          )
          .reduce((s, r) => s + r.revenue, 0),
      );
      const index = tableMonths.indexOf(month);
      return {
        patientDays: isAverage ? avg(patientDays) : (patientDays[index] ?? 0),
        fee: isAverage ? avg(fee) : (fee[index] ?? 0),
      };
    });
    return {
      month,
      groups,
      totalPatientDays: groups.reduce((s, g) => s + g.patientDays, 0),
      totalFee: groups.reduce((s, g) => s + g.fee, 0),
    };
  });
  const common = {
    width: 4000,
    height: 270,
    marginLeft: 82,
    marginRight: 20,
    marginTop: 28,
    marginBottom: 42,
    style: { fontFamily: "var(--jp-font, system-ui)", fontSize: "12px" },
    x: {
      type: "band" as const,
      domain: trendMonths,
      label: null,
      tickFormat: (month: string) => `${Number(month.slice(5, 7))}月`,
    },
  };
  const occupancyOptions: Plot.PlotOptions = {
    ...common,
    y: { label: null, grid: true, tickFormat: (v: number) => `${v}%` },
    marks: [
      Plot.ruleY([100], { stroke: "#94a3b8" }),
      Plot.lineY(priorOccupancySeries, {
        x: "month",
        y: "rate",
        stroke: "#94a3b8",
        marker: true,
        tip: true,
        title: (d) =>
          `前年同月: ${monthLabel(d.previousMonth)}\n病床利用率: ${d.rate.toFixed(1)}%`,
      }),
      Plot.lineY(occupancySeries, {
        x: "month",
        y: "rate",
        stroke: "#1B9E77",
        marker: true,
      }),
      Plot.text(
        occupancySeries.filter((r) => r.month !== selected),
        {
          x: "month",
          y: "rate",
          text: "rateLabel",
          dy: -13,
          fill: "#475569",
          fontWeight: 700,
          fontSize: 13,
        },
      ),
      Plot.dot(selectedOccupancy, {
        x: "month",
        y: "rate",
        r: 7,
        fill: "#e11d48",
        stroke: "white",
        strokeWidth: 2,
      }),
      Plot.text(selectedOccupancy, {
        x: "month",
        y: "rate",
        text: "rateLabel",
        dy: -16,
        fill: "#e11d48",
        fontWeight: 800,
        fontSize: 14,
      }),
    ],
  };
  const feeOptions: Plot.PlotOptions = {
    ...common,
    y: {
      label: null,
      grid: true,
      tickFormat: (v: number) => `${(v / 100_000_000).toFixed(2)}億`,
    },
    marks: [
      Plot.lineY(priorRevenueTotals, {
        x: "month",
        y: "revenue",
        stroke: "#94a3b8",
        marker: true,
        tip: true,
        title: (d) =>
          `前年同月: ${monthLabel(d.previousMonth)}\n入院料: ${feeAmount(d.revenue)}`,
      }),
      Plot.lineY(revenueTotals, {
        x: "month",
        y: "revenue",
        stroke: "#1B9E77",
        marker: true,
      }),
      Plot.text(
        revenueTotals.filter((r) => r.month !== selected),
        {
          x: "month",
          y: "revenue",
          text: (d) => feeAmount(d.revenue),
          dy: -13,
          fill: "#475569",
          fontWeight: 700,
          fontSize: 13,
        },
      ),
      Plot.dot(selectedRevenue, {
        x: "month",
        y: "revenue",
        r: 7,
        fill: "#e11d48",
        stroke: "white",
        strokeWidth: 2,
      }),
      Plot.text(selectedRevenue, {
        x: "month",
        y: "revenue",
        text: "label",
        dy: -16,
        fill: "#e11d48",
        fontWeight: 800,
        fontSize: 14,
      }),
    ],
  };
  const dailyFeeOrder = ["ICU", "ハイケア", "一般病床", "5D"];
  const selectedWardDailyFees = selectedWards
    .filter((r) => r.patientDays > 0 && r.feeRevenue > 0)
    .sort(
      (a, b) => dailyFeeOrder.indexOf(a.unit) - dailyFeeOrder.indexOf(b.unit),
    )
    .map((r) => ({
      ...r,
      label: wardLabel(r.unit),
      dailyFee: r.feeRevenue / r.patientDays,
      dailyFeeLabel: manYen1(r.feeRevenue / r.patientDays),
    }));
  const maxDailyFee = Math.max(
    ...selectedWardDailyFees.map((r) => r.dailyFee),
    1,
  );
  const wardDailyFeeOptions: Plot.PlotOptions = {
    width: 4000,
    height: 250,
    marginLeft: 88,
    marginRight: 80,
    marginTop: 18,
    marginBottom: 40,
    style: { fontFamily: "var(--jp-font, system-ui)", fontSize: "12px" },
    x: {
      grid: true,
      domain: [0, maxDailyFee * 1.12],
      tickFormat: manYen1,
    },
    y: { label: null, domain: selectedWardDailyFees.map((r) => r.label) },
    marks: [
      Plot.barX(selectedWardDailyFees, {
        x: "dailyFee",
        y: "label",
        fill: (d) => WARD_COLORS[d.unit],
        fillOpacity: 0.86,
        tip: true,
        title: (d) =>
          `${d.label}\n入院延数: ${int(d.patientDays)}日\n1日入院単価: ${manYen1(d.dailyFee)}\n入院料: ${feeAmount(d.feeRevenue)}`,
      }),
      Plot.text(selectedWardDailyFees, {
        x: "dailyFee",
        y: "label",
        text: "dailyFeeLabel",
        dx: 8,
        textAnchor: "start",
        fill: "#334155",
        fontWeight: 700,
        fontSize: 13,
      }),
    ],
  };
  return (
    <div className="inpatient-fee-hospital space-y-3">
      <section className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <div className="card p-4">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold">1. 病床利用率</h3>
            <TrendLegend fy={fy} />
          </div>
          <PlotFigure
            options={occupancyOptions}
            responsiveWidth
            className="mt-2"
          />
        </div>
        <div className="card p-4">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold">2. 入院料</h3>
            <TrendLegend fy={fy} />
          </div>
          <PlotFigure options={feeOptions} responsiveWidth className="mt-2" />
        </div>
      </section>
      <section className="card p-4">
        <h3 className="font-semibold">3. 病棟別 入院延数・入院料</h3>
        <div className="mt-3">
          <HospitalStackBar
            label={
              <>
                入院延数
                <br />
                {int(selectedWards.reduce((s, r) => s + r.patientDays, 0))}日
              </>
            }
            rows={selectedWardsForStack}
            field="patientDays"
            format={(v) => `${int(v)}日`}
          />
          <HospitalStackBar
            label={
              <>
                入院料
                <br />
                {feeAmount(selectedWards.reduce((s, r) => s + r.feeRevenue, 0))}
              </>
            }
            rows={selectedWardsForStack}
            field="feeRevenue"
            format={feeAmount}
          />
        </div>
      </section>
      <section className="card p-4">
        <h3 className="font-semibold">4. 病棟別 1日入院料</h3>
        <PlotFigure
          options={wardDailyFeeOptions}
          responsiveWidth
          className="mt-2"
        />
      </section>
      <section className="card p-4 overflow-x-auto">
        <h3 className="font-semibold">5. 月別集計表</h3>
        <table className="w-full text-xs mt-3 min-w-[1560px]">
          <thead>
            <tr className="border-b bg-slate-50">
              <th rowSpan={2} className="text-left p-2">
                年月
              </th>
              <th colSpan={3} className="text-center p-2 border-l">
                全体
              </th>
              {tableWardGroups.map((g) => (
                <th
                  key={g.unit}
                  colSpan={3}
                  className="text-center p-2 border-l"
                >
                  {wardLabel(g.unit)}
                </th>
              ))}
            </tr>
            <tr className="border-b bg-slate-50">
              {["全体", ...tableWardGroups.map((g) => g.unit)].flatMap((g) => [
                <th key={`${g}-days`} className="text-right p-2 border-l">
                  入院延数
                </th>,
                <th key={`${g}-fee`} className="text-right p-2">
                  入院料
                </th>,
                <th key={`${g}-daily-fee`} className="text-right p-2">
                  1日入院料
                </th>,
              ])}
            </tr>
          </thead>
          <tbody>
            {tableRows.map((r) => (
              <tr
                key={r.month}
                onClick={() =>
                  r.month !== "月平均" && setSelectedMonth(r.month)
                }
                className={`border-b ${r.month === selected ? "bg-amber-100 font-semibold" : r.month === "月平均" ? "bg-slate-50 font-semibold" : "cursor-pointer hover:bg-amber-50"}`}
              >
                <td className="p-2">
                  {r.month === "月平均" ? r.month : monthLabel(r.month)}
                </td>
                <td className="p-2 text-right border-l">
                  {int(r.totalPatientDays)}日
                </td>
                <td className="p-2 text-right">{feeAmount(r.totalFee)}</td>
                <td className="p-2 text-right">
                  {r.totalPatientDays > 0
                    ? manYen1(r.totalFee / r.totalPatientDays)
                    : "—"}
                </td>
                {r.groups.flatMap((g, i) => [
                  <td key={`${i}-days`} className="p-2 text-right border-l">
                    {int(g.patientDays)}日
                  </td>,
                  <td key={`${i}-fee`} className="p-2 text-right">
                    {tableWardGroups[i].unit === "一般病床"
                      ? feeAmount(g.fee)
                      : manYen(g.fee)}
                  </td>,
                  <td key={`${i}-daily-fee`} className="p-2 text-right">
                    {g.patientDays > 0 ? manYen1(g.fee / g.patientDays) : "—"}
                  </td>,
                ])}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function HospitalStackBar({
  label,
  rows,
  field,
  format,
}: {
  label: React.ReactNode;
  rows: { unit: string; patientDays: number; feeRevenue: number }[];
  field: "patientDays" | "feeRevenue";
  format: (v: number) => string;
}) {
  const total = rows.reduce((s, r) => s + r[field], 0) || 1;
  return (
    <div className="flex items-center gap-2 mb-3">
      <div className="text-sm font-semibold text-slate-700 w-20 shrink-0 text-right">
        {label}
      </div>
      <div className="flex w-full h-24 rounded overflow-hidden border border-slate-200">
        {rows
          .filter((r) => r[field] > 0)
          .map((r) => {
            const share = (r[field] / total) * 100;
            return (
              <div
                key={r.unit}
                className="flex items-center justify-center text-white overflow-hidden"
                style={{ width: `${share}%`, background: WARD_COLORS[r.unit] }}
                title={`${r.unit}: ${format(r[field])} (${share.toFixed(1)}%)`}
              >
                <div className="text-center leading-tight whitespace-nowrap">
                  <div className="text-sm font-bold">{wardLabel(r.unit)}</div>
                  <div className="text-xs font-semibold">
                    {format(r[field])}
                  </div>
                  <div className="text-xs">{share.toFixed(1)}%</div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
function Summary({
  data,
  months,
  latestMonth,
  categoryByMonth,
  latestRevenue,
  latestMatchRate,
  latestDenominator,
  latestDirectDischarge,
  matchDeltaPt,
  latestReview,
  latestSpecial,
  prevSpecial,
  latestShort,
  openUnit,
  openShortStay,
}: {
  data: Data;
  months: string[];
  latestMonth: string;
  categoryByMonth: CategoryMap;
  latestRevenue: number;
  latestMatchRate: number | null;
  latestDenominator: number;
  latestDirectDischarge: number;
  matchDeltaPt: number | null;
  latestReview: number;
  latestSpecial: number;
  prevSpecial: number;
  latestShort: { revenue: number; patientDays: number; cases: number };
  openUnit: (unit: Unit) => void;
  openShortStay: () => void;
}) {
  return (
    <>
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <ExecutiveCard
          eyebrow="増やす"
          title="高度病床を適切に算定"
          value={pct(latestMatchRate)}
          detail={`対象 ${int(latestDenominator)}日（直接退院 ${int(latestDirectDischarge)}日を含む）`}
          delta={
            matchDeltaPt == null
              ? "比較月なし"
              : `${signed(matchDeltaPt, "pt")} 前月比`
          }
          tone={matchDeltaPt != null && matchDeltaPt < 0 ? "danger" : "primary"}
          onClick={() => openUnit("ICU")}
        />
        <ExecutiveCard
          eyebrow="減らす"
          title="特別入院基本料"
          value={`${int(latestSpecial)}患者日`}
          detail={`${monthLabel(latestMonth)}実績`}
          delta={`${signed(latestSpecial - prevSpecial, "日")} 前月比`}
          tone={latestSpecial > prevSpecial ? "danger" : "good"}
          onClick={() => openUnit("5D")}
        />
        <ExecutiveCard
          eyebrow="短くする"
          title="短期滞在3"
          value={`${int(latestShort.cases)}件`}
          detail={`${int(latestShort.patientDays)}患者日・${manYen(latestShort.revenue)}`}
          delta={
            latestShort.cases > 0
              ? `平均 ${(latestShort.patientDays / latestShort.cases).toFixed(2)}日/件 · 詳細を見る →`
              : "実績なし"
          }
          tone="warm"
          onClick={openShortStay}
        />
      </section>
      <section className="grid grid-cols-1 xl:grid-cols-[1.5fr_1fr] gap-3">
        <div className="card p-4">
          <h3 className="font-semibold">
            {monthLabel(latestMonth)} 入院料売上の構成
          </h3>
          <p className="text-[11px] text-slate-500 mt-1">
            入院料小計・合計 {manYen(latestRevenue)}
          </p>
          <div className="h-11 rounded-lg overflow-hidden flex mt-4 bg-slate-100">
            {CATEGORIES.map((c) => {
              const value =
                categoryByMonth.get(latestMonth)?.get(c)?.revenue ?? 0;
              const width =
                latestRevenue > 0 ? (value / latestRevenue) * 100 : 0;
              return width > 0 ? (
                <div
                  key={c}
                  style={{ width: `${width}%`, background: CATEGORY_COLORS[c] }}
                  className="h-full relative"
                  title={`${c}: ${yen(value)} (${width.toFixed(1)}%)`}
                >
                  {width >= 8 && (
                    <span className="absolute inset-0 flex items-center justify-center text-[10px] text-white font-semibold">
                      {width.toFixed(1)}%
                    </span>
                  )}
                </div>
              ) : null;
            })}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2 mt-4">
            {CATEGORIES.map((c) => (
              <div
                key={c}
                className="flex items-center justify-between gap-2 text-xs"
              >
                <span className="flex items-center gap-1.5">
                  <span
                    className="w-2.5 h-2.5 rounded-sm"
                    style={{ background: CATEGORY_COLORS[c] }}
                  />
                  {c}
                </span>
                <b className="tabular-nums">
                  {manYen(
                    categoryByMonth.get(latestMonth)?.get(c)?.revenue ?? 0,
                  )}
                </b>
              </div>
            ))}
          </div>
        </div>
        <div className="card p-4 border-l-4 border-l-amber-400">
          <div className="text-[11px] text-amber-700 font-semibold">
            今月の経営問い
          </div>
          <h3 className="font-semibold mt-1">
            要確認 {int(latestReview)}患者日を理由別に確定する
          </h3>
          <p className="text-xs text-slate-600 mt-2 leading-relaxed">
            要確認は取り漏れ確定ではありません。適正な非算定・修正済み・未解決に分け、未解決だけを行動対象にします。
          </p>
          <div className="mt-4 space-y-2 text-xs">
            <ActionRow
              owner="医事"
              action="適格性・未算定理由を確定"
              cadence="D+1"
            />
            <ActionRow
              owner="病棟"
              action="転棟・直接退院を照合"
              cadence="週次"
            />
            <ActionRow
              owner="経営"
              action="未解決と修正完了を確認"
              cadence="月次"
            />
          </div>
        </div>
      </section>
      <section className="card p-4 overflow-x-auto">
        <h3 className="font-semibold">月別売上</h3>
        <table className="w-full text-xs mt-3 min-w-[900px]">
          <thead>
            <tr className="border-b">
              <th className="text-left p-2">月</th>
              {CATEGORIES.map((c) => (
                <th key={c} className="text-right p-2">
                  {c}
                </th>
              ))}
              <th className="text-right p-2 bg-slate-50">合計</th>
            </tr>
          </thead>
          <tbody>
            {months.map((month) => {
              const vals = CATEGORIES.map(
                (c) => categoryByMonth.get(month)?.get(c)?.revenue ?? 0,
              );
              return (
                <tr key={month} className="border-b border-slate-100">
                  <td className="p-2 font-medium">{monthLabel(month)}</td>
                  {vals.map((v, i) => (
                    <td
                      key={CATEGORIES[i]}
                      className="text-right p-2 tabular-nums"
                    >
                      {manYen(v)}
                    </td>
                  ))}
                  <td className="text-right p-2 font-semibold bg-slate-50">
                    {manYen(vals.reduce((a, b) => a + b, 0))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
      <RevisionTable rows={data.revision} />
    </>
  );
}
void Summary;

function UnitPage({
  unit,
  data,
  dateFrom,
  dateTo,
}: {
  unit: Unit;
  data: Data;
  dateFrom: string;
  dateTo: string;
}) {
  const allRows = data.occupancy.filter((r) => r.unit === unit);
  const rows = allRows.filter((r) => r.month >= dateFrom && r.month <= dateTo);
  const months = rows.map((r) => r.month);
  const fy = fiscalYearOf(dateTo);
  const trendMonths = fyMonthDates(fy);
  const currentTrendMonths = trendMonths.filter(
    (month) => month <= dateTo && months.includes(month),
  );
  const tableMonths = months.slice(-12);
  const trendRows = rows.filter((r) => currentTrendMonths.includes(r.month));
  const [selectedMonth, setSelectedMonth] = useState(months.at(-1) ?? "");
  const selected = months.includes(selectedMonth)
    ? selectedMonth
    : (months.at(-1) ?? "");
  const allRoomSource = data.roomDetail.filter(
    (r) => r.room === (unit === "5D" ? "5D病棟" : unit),
  );
  const roomSource = allRoomSource.filter(
    (r) => r.month >= dateFrom && r.month <= dateTo,
  );
  const trendRoomSource = roomSource.filter((r) =>
    currentTrendMonths.includes(r.month),
  );
  const feeName =
    unit === "ICU"
      ? "ICU管理料"
      : unit === "5D"
        ? "回復期入院料"
        : unit === "一般病床"
          ? "急性期一般入院料"
          : "ハイケア管理料";
  const primaryCategory = expectedCategory(unit);
  const secondaryCategory =
    unit === "5D"
      ? "特別入院基本料"
      : unit === "一般病床"
        ? "短期滞在3"
        : "急性期一般";
  const rateName = `${feeName}${unit === "一般病床" ? "構成率" : "算定率"}`;
  const feeDisplay = unit === "一般病床" ? feeAmount : manYen;
  const feeTick =
    unit === "一般病床"
      ? (v: number) => `${(v / 100_000_000).toFixed(2)}億`
      : (v: number) => `${Math.round(v / 10_000)}万`;
  const allOccupancySeries = rows.map((r) => ({
    ...r,
    rate: r.occupancyRate == null ? null : r.occupancyRate * 100,
    rateLabel: pct(r.occupancyRate),
  }));
  const occupancySeries = allOccupancySeries.filter((r) =>
    currentTrendMonths.includes(r.month),
  );
  const priorMonth = (month: string) =>
    `${Number(month.slice(0, 4)) - 1}${month.slice(4)}`;
  const priorOccupancySeries = trendMonths
    .map((month) => {
      const previousMonth = priorMonth(month);
      const r = allRows.find((x) => x.month === previousMonth);
      return r && r.occupancyRate != null
        ? { month, previousMonth, rate: r.occupancyRate * 100 }
        : null;
    })
    .filter(
      (r): r is { month: string; previousMonth: string; rate: number } =>
        r != null,
    );
  const feeSeries = currentTrendMonths.map((month) => ({
    month,
    revenue: trendRoomSource
      .filter((r) => r.month === month)
      .reduce((s, r) => s + r.revenue, 0),
  }));
  const priorFeeSeries = trendMonths
    .map((month) => ({
      month,
      previousMonth: priorMonth(month),
      revenue: allRoomSource
        .filter((r) => r.month === priorMonth(month))
        .reduce((s, r) => s + r.revenue, 0),
    }))
    .filter((r) => r.revenue > 0);
  const selectedOccupancy = occupancySeries.filter((r) => r.month === selected);
  const selectedFee = feeSeries
    .filter((r) => r.month === selected)
    .map((r) => ({ ...r, label: feeDisplay(r.revenue) }));
  const common = {
    width: 4000,
    height: 270,
    marginLeft: 82,
    marginRight: 20,
    marginTop: 28,
    marginBottom: 42,
    style: { fontFamily: "var(--jp-font, system-ui)", fontSize: "12px" },
    x: {
      type: "band" as const,
      domain: trendMonths,
      label: null,
      tickFormat: (month: string) => `${Number(month.slice(5, 7))}月`,
    },
  };
  const occupancyOptions: Plot.PlotOptions = {
    ...common,
    y: { label: null, grid: true, tickFormat: (v: number) => `${v}%` },
    marks: [
      Plot.ruleY([100], { stroke: "#94a3b8" }),
      Plot.lineY(priorOccupancySeries, {
        x: "month",
        y: "rate",
        stroke: "#94a3b8",
        marker: true,
        tip: true,
        title: (d) =>
          `前年同月: ${monthLabel(d.previousMonth)}\n病床利用率: ${d.rate.toFixed(1)}%`,
      }),
      Plot.lineY(occupancySeries, {
        x: "month",
        y: "rate",
        stroke: "#1B9E77",
        marker: true,
        tip: true,
      }),
      Plot.text(
        occupancySeries.filter((r) => r.month !== selected),
        {
          x: "month",
          y: "rate",
          text: "rateLabel",
          dy: -13,
          fill: "#475569",
          fontWeight: 700,
          fontSize: 13,
        },
      ),
      Plot.dot(selectedOccupancy, {
        x: "month",
        y: "rate",
        r: 7,
        fill: "#e11d48",
        stroke: "white",
        strokeWidth: 2,
      }),
      Plot.text(selectedOccupancy, {
        x: "month",
        y: "rate",
        text: "rateLabel",
        dy: -16,
        fill: "#e11d48",
        fontWeight: 800,
        fontSize: 14,
      }),
    ],
  };
  const feeTrendOptions: Plot.PlotOptions = {
    ...common,
    y: { label: null, grid: true, tickFormat: feeTick },
    marks: [
      Plot.lineY(priorFeeSeries, {
        x: "month",
        y: "revenue",
        stroke: "#94a3b8",
        marker: true,
        tip: true,
        title: (d) =>
          `前年同月: ${monthLabel(d.previousMonth)}\n入院料: ${feeDisplay(d.revenue)}`,
      }),
      Plot.lineY(feeSeries, {
        x: "month",
        y: "revenue",
        stroke: "#1B9E77",
        marker: true,
        tip: true,
      }),
      Plot.text(
        feeSeries.filter((r) => r.month !== selected),
        {
          x: "month",
          y: "revenue",
          text: (d) => feeDisplay(d.revenue),
          dy: -13,
          fill: "#475569",
          fontWeight: 700,
          fontSize: 13,
        },
      ),
      Plot.dot(selectedFee, {
        x: "month",
        y: "revenue",
        r: 7,
        fill: "#e11d48",
        stroke: "white",
        strokeWidth: 2,
      }),
      Plot.text(selectedFee, {
        x: "month",
        y: "revenue",
        text: "label",
        dy: -16,
        fill: "#e11d48",
        fontWeight: 800,
        fontSize: 14,
      }),
    ],
  };
  const billingMap = new Map<
    string,
    { month: string; category: string; patientDays: number }
  >();
  for (const r of roomSource) {
    const key = `${r.month}|${r.billedCategory}`;
    const value = billingMap.get(key) ?? {
      month: r.month,
      category: r.billedCategory,
      patientDays: 0,
    };
    value.patientDays += r.patientDays;
    billingMap.set(key, value);
  }
  const billingRowsAll = [...billingMap.values()].sort(
    (a, b) =>
      a.month.localeCompare(b.month) ||
      (a.category === expectedCategory(unit)
        ? -1
        : b.category === expectedCategory(unit)
          ? 1
          : a.category === "急性期一般"
            ? -1
            : b.category === "急性期一般"
              ? 1
              : a.category.localeCompare(b.category, "ja")),
  );
  const billingRows = billingRowsAll.filter((r) =>
    currentTrendMonths.includes(r.month),
  );
  const billingCategories = ROOM_CATEGORIES.filter((c) =>
    billingRows.some((r) => r.category === c),
  );
  const billingTotals = trendRows.map((r) => ({
    month: r.month,
    patientDays: r.denominatorPatientDays,
  }));
  const expectedBillingLabels = billingRows
    .filter((r) => r.category === expectedCategory(unit))
    .map((r) => {
      const total =
        billingTotals.find((x) => x.month === r.month)?.patientDays ?? 0;
      return {
        ...r,
        midpoint: r.patientDays / 2,
        countLabel: `${int(r.patientDays)}日`,
        rateLabel: pct(total > 0 ? r.patientDays / total : null),
      };
    });
  const acuteBillingLabels = billingRows
    .filter((r) => r.category === "急性期一般")
    .map((r) => {
      const total =
        billingTotals.find((x) => x.month === r.month)?.patientDays ?? 0;
      // 主区分の上に積むため、急性期一般セグメントの中央へ配置する。
      const expected =
        billingRows.find(
          (x) => x.month === r.month && x.category === expectedCategory(unit),
        )?.patientDays ?? 0;
      const showRate = total > 0 && r.patientDays / total >= 0.16;
      return {
        ...r,
        midpoint: expected + r.patientDays / 2,
        countLabel: `${int(r.patientDays)}日`,
        rateLabel: pct(total > 0 ? r.patientDays / total : null),
        showRate,
      };
    });
  const acuteBillingLabelsWithRate = acuteBillingLabels.filter(
    (d) => d.showRate,
  );
  const acuteBillingLabelsCountOnly = acuteBillingLabels.filter(
    (d) => !d.showRate,
  );
  const billingOptions: Plot.PlotOptions = {
    ...common,
    marginTop: 38,
    y: {
      label: null,
      grid: true,
      tickFormat: (v: number) => `${Math.round(v)}日`,
    },
    color: {
      domain: billingCategories,
      range: billingCategories.map((c) => CATEGORY_COLORS[c]),
      legend: true,
    },
    marks: [
      Plot.barY(billingRows, {
        x: "month",
        y: "patientDays",
        fill: "category",
        tip: true,
        insetLeft: 5,
        insetRight: 5,
      }),
      Plot.text(billingTotals, {
        x: "month",
        y: "patientDays",
        text: (d) => `${int(d.patientDays)}日`,
        dy: -12,
        fill: "#334155",
        fontWeight: 700,
      }),
      Plot.text(expectedBillingLabels, {
        x: "month",
        y: "midpoint",
        text: "countLabel",
        dy: -7,
        fill: "white",
        fontWeight: 700,
      }),
      Plot.text(expectedBillingLabels, {
        x: "month",
        y: "midpoint",
        text: "rateLabel",
        dy: 8,
        fill: "white",
        fontWeight: 700,
      }),
      Plot.text(acuteBillingLabelsWithRate, {
        x: "month",
        y: "midpoint",
        text: "countLabel",
        dy: -7,
        fill: "white",
        fontWeight: 700,
      }),
      Plot.text(acuteBillingLabelsWithRate, {
        x: "month",
        y: "midpoint",
        text: "rateLabel",
        dy: 8,
        fill: "white",
        fontWeight: 700,
      }),
      Plot.text(acuteBillingLabelsCountOnly, {
        x: "month",
        y: "midpoint",
        text: "countLabel",
        fill: "white",
        fontWeight: 700,
      }),
    ],
  };
  // ICUでは「算定率」と「患者日内訳」を同時に読みたい。案5は重ね合わせ、案6は上下分割で比較する。
  const managementRateSeries = trendRows.map((r) => ({
    month: r.month,
    rate:
      r.denominatorPatientDays > 0
        ? (r.matchedPatientDays / r.denominatorPatientDays) * 100
        : null,
    rateLabel:
      r.denominatorPatientDays > 0
        ? `${((r.matchedPatientDays / r.denominatorPatientDays) * 100).toFixed(1)}%`
        : "—",
  }));
  const priorManagementRateSeries = trendMonths
    .map((month) => {
      const previousMonth = priorMonth(month);
      const r = allRows.find((row) => row.month === previousMonth);
      return r && r.denominatorPatientDays > 0
        ? {
            month,
            previousMonth,
            rate: (r.matchedPatientDays / r.denominatorPatientDays) * 100,
          }
        : null;
    })
    .filter(
      (r): r is { month: string; previousMonth: string; rate: number } =>
        r != null,
    );
  const rateLineOptions: Plot.PlotOptions = {
    ...common,
    height: 205,
    marginTop: 28,
    y: {
      label: null,
      grid: true,
      nice: true,
      tickFormat: (v: number) => `${v}%`,
    },
    marks: [
      Plot.ruleY([100], { stroke: "#94a3b8" }),
      Plot.lineY(priorManagementRateSeries, {
        x: "month",
        y: "rate",
        stroke: "#94a3b8",
        marker: true,
        tip: true,
        title: (d) =>
          `前年同月: ${monthLabel(d.previousMonth)}\n${rateName}: ${d.rate.toFixed(1)}%`,
      }),
      Plot.lineY(managementRateSeries, {
        x: "month",
        y: "rate",
        stroke: "#1B9E77",
        marker: true,
        tip: true,
      }),
      Plot.text(
        managementRateSeries.filter((r) => r.month !== selected),
        {
          x: "month",
          y: "rate",
          text: "rateLabel",
          dy: -13,
          fill: "#475569",
          fontWeight: 700,
          fontSize: 13,
        },
      ),
      Plot.dot(
        managementRateSeries.filter((r) => r.month === selected),
        {
          x: "month",
          y: "rate",
          r: 7,
          fill: "#e11d48",
          stroke: "white",
          strokeWidth: 2,
        },
      ),
      Plot.text(
        managementRateSeries.filter((r) => r.month === selected),
        {
          x: "month",
          y: "rate",
          text: "rateLabel",
          dy: -16,
          fill: "#e11d48",
          fontWeight: 800,
          fontSize: 14,
        },
      ),
    ],
  };
  const revenueMap = new Map<
    string,
    { month: string; category: string; revenue: number }
  >();
  for (const r of roomSource) {
    const key = `${r.month}|${r.billedCategory}`;
    const value = revenueMap.get(key) ?? {
      month: r.month,
      category: r.billedCategory,
      revenue: 0,
    };
    value.revenue += r.revenue;
    revenueMap.set(key, value);
  }
  // 主区分を積み上げの下段に固定し、そのセグメント内へ金額を直接表示する。
  const revenueRows = [...revenueMap.values()].sort(
    (a, b) =>
      a.month.localeCompare(b.month) ||
      (a.category === expectedCategory(unit)
        ? -1
        : b.category === expectedCategory(unit)
          ? 1
          : a.category.localeCompare(b.category, "ja")),
  );
  const unitTableRows = ["月平均", ...[...tableMonths].reverse()].map(
    (month) => {
      const average = month === "月平均";
      const avg = (values: number[]) =>
        values.length ? values.reduce((s, v) => s + v, 0) / values.length : 0;
      const occupancy = average
        ? avg(
            allOccupancySeries
              .filter((r) => tableMonths.includes(r.month))
              .map((r) => r.rate ?? 0),
          )
        : (allOccupancySeries.find((r) => r.month === month)?.rate ?? 0);
      const category = (name: string) => {
        const applicableMonths =
          name === "短期滞在3"
            ? tableMonths.filter((m) => monthCode(m) >= SHORT_STAY3_START_MONTH)
            : tableMonths;
        const unavailable =
          name === "短期滞在3" &&
          !average &&
          monthCode(month) < SHORT_STAY3_START_MONTH;
        const days = applicableMonths.map((m) =>
          billingRowsAll
            .filter((r) => r.month === m && r.category === name)
            .reduce((s, r) => s + r.patientDays, 0),
        );
        const revenue = applicableMonths.map((m) =>
          revenueRows
            .filter((r) => r.month === m && r.category === name)
            .reduce((s, r) => s + r.revenue, 0),
        );
        const index = applicableMonths.indexOf(month);
        return {
          days: unavailable ? null : average ? avg(days) : (days[index] ?? 0),
          revenue: unavailable
            ? null
            : average
              ? avg(revenue)
              : (revenue[index] ?? 0),
        };
      };
      const focused = category(primaryCategory);
      const denominator = average
        ? avg(
            rows
              .filter((r) => tableMonths.includes(r.month))
              .map((r) => r.denominatorPatientDays),
          )
        : (rows.find((r) => r.month === month)?.denominatorPatientDays ??
          0);
      const acute = category(secondaryCategory);
      return {
        month,
        occupancy,
        managementRate:
          denominator > 0 && focused.days != null
            ? focused.days / denominator
            : null,
        focused: {
          ...focused,
          dailyFee:
            focused.days != null && focused.revenue != null && focused.days > 0
              ? focused.revenue / focused.days
              : null,
        },
        acute: {
          ...acute,
          dailyFee:
            acute.days != null && acute.revenue != null && acute.days > 0
              ? acute.revenue / acute.days
              : null,
        },
      };
    },
  );

  const useIcuLayout = true;
  const unitTrendHeight = unit === "一般病床" ? 300 : 205;
  return (
    <>
      <div
        className={
          useIcuLayout
            ? "inpatient-fee-icu grid grid-cols-1 xl:grid-cols-[minmax(0,0.88fr)_minmax(0,1.12fr)] xl:auto-rows-fr gap-3 items-stretch"
            : "contents"
        }
      >
        <section
          className={
            useIcuLayout ? "contents" : "grid grid-cols-1 xl:grid-cols-2 gap-3"
          }
        >
          <div
            className={
              useIcuLayout ? "card p-4 h-full xl:col-start-1" : "card p-4"
            }
          >
            {useIcuLayout ? (
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-semibold">1. 入院料</h3>
                <TrendLegend fy={fy} />
              </div>
            ) : (
              <>
                <h3 className="font-semibold">1. 病床利用率</h3>
                <p className="text-[11px] text-slate-500 mt-1">
                  23:59在院患者日 ÷
                  使用可能病床日。緑は今年度、グレーは前年同月です。
                </p>
              </>
            )}
            <PlotFigure
              options={
                useIcuLayout
                  ? { ...feeTrendOptions, height: unitTrendHeight }
                  : feeTrendOptions
              }
              responsiveWidth
              className="mt-2"
            />
          </div>
          <div
            className={
              useIcuLayout ? "card p-4 h-full xl:col-start-1" : "card p-4"
            }
          >
            {useIcuLayout ? (
              <>
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-semibold">2. 病床利用率</h3>
                  <TrendLegend fy={fy} />
                </div>
                <PlotFigure
                  options={{ ...occupancyOptions, height: unitTrendHeight }}
                  responsiveWidth
                  className="mt-2"
                />
              </>
            ) : (
              <>
                <h3 className="font-semibold">2. 入院料</h3>
                <p className="text-[11px] text-slate-500 mt-1">
                  会計データの入院料小計。緑は今年度、グレーは前年同月です。
                </p>
                <PlotFigure
                  options={feeTrendOptions}
                  responsiveWidth
                  className="mt-2"
                />
              </>
            )}
          </div>
        </section>
        {unit !== "一般病床" && (
          <div className={useIcuLayout ? "contents" : ""}>
            <section
              className={
                useIcuLayout ? "card p-4 h-full xl:col-start-1" : "card p-4"
              }
            >
              {useIcuLayout ? (
                <>
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-semibold">3. {rateName}</h3>
                    <TrendLegend fy={fy} />
                  </div>
                  <PlotFigure
                    options={{ ...rateLineOptions, height: 205 }}
                    responsiveWidth
                    className="mt-2"
                  />
                </>
              ) : (
                <>
                  <h3 className="font-semibold">3. {feeName}算定率</h3>
                  <p className="text-[11px] text-slate-500 mt-1">
                    入院患者延数＝23:59在院患者日＋当該病棟から直接退院した患者日（他病棟への転棟は除外）。分子は
                    {expectedCategoryLabel(unit)}算定患者日です。
                  </p>
                  <PlotFigure
                    options={billingOptions}
                    responsiveWidth
                    className="mt-2"
                  />
                </>
              )}
            </section>
          </div>
        )}
        {useIcuLayout && (
          <section
            className={`card p-4 h-full xl:col-start-2 xl:row-start-1 ${unit === "一般病床" ? "xl:row-span-2" : "xl:row-span-3"}`}
          >
            <h3 className="font-semibold">
              {unit === "一般病床" ? "3." : "4."} 月別集計表
            </h3>
            <p className="text-[11px] text-slate-500 mt-1">
              直近12か月。行を選択すると、左のグラフの対象月が切り替わります。
            </p>
            <table className="w-full table-fixed text-[13px] mt-4 [&_th]:px-2 [&_th]:py-3 [&_td]:px-2 [&_td]:py-3 [&_tbody_tr]:h-12">
              <colgroup>
                <col className="w-[14%]" />
                <col className="w-[14.33%]" />
                <col className="w-[14.33%]" />
                <col className="w-[14.33%]" />
                <col className="w-[14.33%]" />
                <col className="w-[14.33%]" />
                <col className="w-[14.33%]" />
              </colgroup>
              <thead>
                <tr className="border-b bg-slate-50">
                  <th rowSpan={2} className="text-left">
                    年月
                  </th>
                  <th colSpan={3} className="text-center border-l">
                    {primaryCategory}
                  </th>
                  <th colSpan={3} className="text-center border-l">
                    {secondaryCategory}
                  </th>
                </tr>
                <tr className="border-b bg-slate-50">
                  <th className="text-right border-l">入院延数</th>
                  <th className="text-right">入院料</th>
                  <th className="text-right">1日入院料</th>
                  <th className="text-right border-l">入院延数</th>
                  <th className="text-right">入院料</th>
                  <th className="text-right">1日入院料</th>
                </tr>
              </thead>
              <tbody>
                {unitTableRows.map((r) => (
                  <tr
                    key={r.month}
                    onClick={() =>
                      r.month !== "月平均" && setSelectedMonth(r.month)
                    }
                    className={`border-b ${r.month === selected ? "bg-amber-100 font-semibold" : r.month === "月平均" ? "bg-slate-50 font-semibold" : "cursor-pointer hover:bg-amber-50"}`}
                  >
                    <td>
                      {r.month === "月平均" ? r.month : monthLabel(r.month)}
                    </td>
                    <td className="text-right border-l">
                      {r.focused.days == null ? "" : int(r.focused.days)}
                    </td>
                    <td className="text-right">
                      {r.focused.revenue == null
                        ? ""
                        : manYen(r.focused.revenue)}
                    </td>
                    <td className="text-right">
                      {r.focused.dailyFee == null
                        ? "—"
                        : manYen1(r.focused.dailyFee)}
                    </td>
                    <td className="text-right border-l">
                      {r.acute.days == null ? "" : int(r.acute.days)}
                    </td>
                    <td className="text-right">
                      {r.acute.revenue == null ? "" : manYen(r.acute.revenue)}
                    </td>
                    <td className="text-right">
                      {r.acute.dailyFee == null
                        ? "—"
                        : manYen1(r.acute.dailyFee)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </div>
    </>
  );
}

function TrendLegend({ fy }: { fy: number }) {
  return (
    <div className="flex items-center gap-2 shrink-0 text-[10px] text-slate-500">
      <span className="flex items-center gap-1">
        <i className="w-4 border-t-2 border-[#1B9E77]" />
        緑：{fy}年度
      </span>
      <span className="flex items-center gap-1">
        <i className="w-4 border-t-2 border-slate-400" />
        グレー：{fy - 1}年度
      </span>
    </div>
  );
}

function RevisionTable({ rows }: { rows: Data["revision"] }) {
  return (
    <details className="card p-4">
      <summary className="font-semibold cursor-pointer">
        主要入院料の改定前後（令和6年度 → 令和8年度）
      </summary>
      <div className="overflow-x-auto">
        <table className="w-full text-xs mt-4 min-w-[960px]">
          <thead>
            <tr className="border-b">
              <th className="text-left p-2">区分</th>
              <th className="text-left p-2">支払方式</th>
              <th className="text-left p-2">日数帯・項目</th>
              <th className="text-right p-2">改定前</th>
              <th className="text-right p-2">改定後</th>
              <th className="text-right p-2">点数差</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.order} className="border-b border-slate-100">
                <td className="p-2 font-semibold">{r.category}</td>
                <td className="p-2">{r.payment}</td>
                <td className="p-2">{r.dayBand}</td>
                <td className="p-2 text-right">
                  {r.beforePoints == null ? "新設" : `${int(r.beforePoints)}点`}
                </td>
                <td className="p-2 text-right font-semibold">
                  {int(r.afterPoints)}点
                </td>
                <td className="p-2 text-right">
                  {r.pointDelta == null ? "—" : `${signed(r.pointDelta)}点`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}
function ExecutiveCard({
  eyebrow,
  title,
  value,
  detail,
  delta,
  tone,
  onClick,
}: {
  eyebrow: string;
  title: string;
  value: string;
  detail: string;
  delta: string;
  tone: "primary" | "good" | "danger" | "warm";
  onClick?: () => void;
}) {
  const styles = {
    primary: "border-indigo-300 bg-indigo-50 text-indigo-950",
    good: "border-emerald-300 bg-emerald-50 text-emerald-950",
    danger: "border-rose-300 bg-rose-50 text-rose-950",
    warm: "border-amber-300 bg-amber-50 text-amber-950",
  };
  return (
    <button
      onClick={onClick}
      className={`rounded-lg border p-4 text-left hover:shadow-md transition ${styles[tone]}`}
    >
      <div className="text-[10px] font-bold tracking-wider opacity-70">
        {eyebrow}
      </div>
      <div className="text-sm font-semibold mt-1">{title}</div>
      <div className="text-3xl font-bold tabular-nums mt-3">{value}</div>
      <div className="text-xs mt-1 opacity-70">{detail}</div>
      <div className="text-xs font-semibold mt-3 pt-2 border-t border-current/15">
        {delta}
      </div>
    </button>
  );
}
function ActionRow({
  owner,
  action,
  cadence,
}: {
  owner: string;
  action: string;
  cadence: string;
}) {
  return (
    <div className="grid grid-cols-[42px_1fr_38px] gap-2">
      <b>{owner}</b>
      <span className="text-slate-600">{action}</span>
      <b className="text-right text-indigo-700">{cadence}</b>
    </div>
  );
}
